import { useCallback, useEffect, useRef, useState } from 'react'

/** Durée de maintien, en millisecondes. */
const HOLD_MS = 1500

/**
 * Un bouton qu'il faut MAINTENIR pour déclencher l'action.
 *
 * Réservé à la chasse légendaire, et pour une raison précise : engager une flotte
 * sur la bête est irréversible et coûte, en cas d'échec, la totalité des coques
 * engagées, soit des semaines de production. Une confirmation qu'on clique par
 * réflexe ne serait pas à la hauteur ; un geste tenu, si.
 *
 * Le geste doit se VOIR. La barre de progression est un enfant NORMAL du bouton,
 * dans le flux d'un conteneur flex : ni positionnement absolu (un <button> n'est
 * pas un bloc conteneur fiable pour ses descendants absolus), ni astuce de
 * `background-size`. Deux tentatives se sont perdues là ; celle-ci ne dépend
 * d'aucune particularité de moteur de rendu.
 *
 * Accessibilité : le maintien fonctionne aussi au clavier (Espace ou Entrée
 * maintenus), le relâchement annule, le bouton s'annonce comme `progressbar`
 * pendant le geste, et sous `prefers-reduced-motion` rien ne s'anime (la jauge se
 * remplit par paliers, sans transition).
 */
export function HoldToConfirm({
  label,
  holdingLabel = 'Continue…',
  onConfirm,
  disabled = false,
  className = '',
}: {
  label: string
  holdingLabel?: string
  onConfirm: () => void
  disabled?: boolean
  className?: string
}) {
  const [progress, setProgress] = useState(0)
  const raf = useRef<number | null>(null)
  const start = useRef<number | null>(null)
  const fired = useRef(false)

  const stop = useCallback(() => {
    if (raf.current !== null) cancelAnimationFrame(raf.current)
    raf.current = null
    start.current = null
    setProgress(0)
  }, [])

  useEffect(() => stop, [stop])

  const tick = useCallback(
    (ts: number) => {
      if (start.current === null) start.current = ts
      const p = Math.min(1, (ts - start.current) / HOLD_MS)
      setProgress(p)
      if (p >= 1) {
        raf.current = null
        start.current = null
        if (!fired.current) {
          fired.current = true
          onConfirm()
          // Laisse la barre pleine un instant, puis la vide : le capitaine voit
          // que son geste a porté.
          window.setTimeout(() => {
            fired.current = false
            setProgress(0)
          }, 400)
        }
        return
      }
      raf.current = requestAnimationFrame(tick)
    },
    [onConfirm],
  )

  const begin = useCallback(() => {
    if (disabled || fired.current || raf.current !== null) return
    raf.current = requestAnimationFrame(tick)
  }, [disabled, tick])

  const pct = Math.round(progress * 100)
  const secondsLeft = (Math.max(0, HOLD_MS * (1 - progress)) / 1000).toFixed(1).replace('.', ',')

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={begin}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault()
          begin()
        }
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') stop()
      }}
      onBlur={stop}
      aria-describedby="hold-hint"
      data-holding={progress > 0 ? 'true' : undefined}
      role={progress > 0 ? 'progressbar' : undefined}
      aria-valuemin={progress > 0 ? 0 : undefined}
      aria-valuemax={progress > 0 ? 100 : undefined}
      aria-valuenow={progress > 0 ? pct : undefined}
      className={`flex w-full flex-col items-stretch gap-2 rounded-md border border-red-500/60 bg-red-500/20 px-3 py-2.5 text-sm font-semibold text-red-100 hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    >
      <span className="flex items-center justify-center gap-2">
        {progress > 0 ? holdingLabel : label}
        {progress > 0 && <span className="tabular-nums text-sand-200">{secondsLeft} s</span>}
      </span>
      {/* Le loader : un enfant en flux, rien de plus. Son rail est visible au
          repos, si bien que le bouton annonce le geste avant qu'on appuie. */}
      <span className="h-2 w-full overflow-hidden rounded-full bg-sand-100/20">
        <span
          className="block h-full rounded-full bg-sand-300 motion-safe:transition-[width] motion-safe:duration-75"
          style={{ width: `${pct}%` }}
        />
      </span>
    </button>
  )
}
